import { useState, useEffect } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  FileText,
  Activity,
  Database,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { AppointmentService } from '../../../services/api/appointment.service';
import { AppointmentSummaryResponse } from './types';
import SummaryTab from './SummaryTab';
import VitalsTab from './VitalsTab';
import HealthRecordsTab from './HealthRecordsTab';
import PrescriptionsTab from './PrescriptionsTab';
import toast from 'react-hot-toast';

const AppointmentDetailsLayout = () => {
  const { appointmentId } = useParams({ strict: false }) as any;

  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AppointmentSummaryResponse | null>(null);

  const fetchSummary = async () => {
    if (!appointmentId) return;
    try {
      setLoading(true);
      const response = await AppointmentService.getAppointmentSummary(appointmentId);
      if (response.success) {
        setData(response);
      } else {
        toast.error(response.message || "Failed to fetch appointment summary");
      }
    } catch (error) {
      toast.error("An error occurred while fetching details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [appointmentId]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
          <p className="text-gray-500 font-medium">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-gray-500">No data found for this appointment.</p>
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-emerald-600 font-medium hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Go Back
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'vitals', label: 'Vitals', icon: Activity },
    { id: 'healthRecords', label: 'Health Records', icon: Database },
    { id: 'prescriptions', label: 'Prescriptions', icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Appointment Details</h1>
            <p className="text-sm text-gray-500">Appointment Id : {data?.appointmentSummary?.appointment?.dhrId}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto space-x-1 bg-white p-1 rounded-xl shadow-sm mb-6 border border-gray-100">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${isActive
                  ? 'bg-emerald-50 text-emerald-600 shadow-sm border border-emerald-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 min-h-[600px]">
          {activeTab === 'summary' && <SummaryTab data={data.appointmentSummary} reload={fetchSummary} />}
          {activeTab === 'vitals' && <VitalsTab data={data.appointmentSummary} reload={fetchSummary} />}
          {activeTab === 'healthRecords' && <HealthRecordsTab data={data.appointmentSummary} reload={fetchSummary} />}
          {activeTab === 'prescriptions' && <PrescriptionsTab data={data.appointmentSummary} reload={fetchSummary} />}
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailsLayout;
